import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	CreateDepositPromotionRequest,
	CreditType,
	GameType,
} from '@/modules/promotion/type'
import { useDateFilter } from '@/common/hooks/useDateFilter'

import { Button } from '@/common/ui/button'
import { DepositPromotionFormValues } from './components/FormSchema'
import { depositPromotionFormSchema } from './components/FormSchema'

import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormDescription,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Checkbox } from '@/common/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/common/ui/radio-group'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import { Editor } from '@/common/components/Editor'
import { ConstraintSelector } from '../ConstraintSelector'
import UploadImage from '@/common/components/UploadImage'
import useMutationCreatePromotionBannerFileUrl from '@/modules/operator/api/hooks/useMutationCreatePromotionBannerFileUrl'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import React, { useState } from 'react'
import { GameProviderSelector } from '../GameProviderSelector'
import { Separator } from '@/common/ui/separator'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import useQueryGetRankName from '@/modules/manage-rank/api/hooks/useQueryGetRankName'
import useQueryGetGameProviderList from '@/modules/game/api/hooks/useQueryGetGameProviderList'
import ProviderVersionSettings from '@/common/components/ProviderVersionSettings'
import { PresetPriorityHint } from '@/common/components/PresetPriorityHint'
import { RtpPresetInfoTooltip } from '../RtpPresetInfoTooltip'
import { RtpGuideButton } from '../RtpGuideModal'
import { RtpConfirmDialog } from '../RtpConfirmDialog'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'
import useQueryGetPlayerTags from '@/modules/tags/api/hooks/useQueryGetPlayerTags'
import { Badge } from '@/common/ui/badge'
import { X, Search, TriangleAlert } from 'lucide-react'

interface DepositPromotionFormProps {
	defaultValues?: Partial<DepositPromotionFormValues>
	onSubmit: (data: CreateDepositPromotionRequest) => void
	mode: 'create' | 'edit'
}

const preventMinusInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
	if (e.key === '-' || e.key === 'e') {
		e.preventDefault()
	}
}

export default function DepositPromotionForm({
	defaultValues,
	onSubmit,
	mode,
}: DepositPromotionFormProps) {
	const { dateFilter, setDateFilter } = useDateFilter()
	const navigate = useNavigate()

	const { data: presets } = useQueryGetPresetId()
	const { data: ranks } = useQueryGetRankName()
	const { data: allTags, isLoading: isLoadingTags } = useQueryGetPlayerTags()
	const [tagSearchQuery, setTagSearchQuery] = React.useState('')

	const [presetThresholdType, setPresetThresholdType] = useState<
		'FIXED' | 'MULTIPLIER'
	>('FIXED')
	const [showRtpConfirm, setShowRtpConfirm] = useState(false)
	const [pendingSubmit, setPendingSubmit] = useState<(() => void) | null>(null)

	// Map ranks to select options with index-based ordering (1, 2, 3...)
	const rankOptions = React.useMemo(
		() =>
			ranks?.map((rank, index) => ({
				label: rank.name,
				value: index + 1, // 1-based indexing
			})) || [],
		[ranks],
	)

	// Filter tags based on search query
	const filteredTags = React.useMemo(() => {
		if (!allTags) return []
		if (!tagSearchQuery.trim()) return allTags

		const query = tagSearchQuery.toLowerCase()
		return allTags.filter((tag) => tag.name.toLowerCase().includes(query))
	}, [allTags, tagSearchQuery])

	// Initialize date filter with existing dates when editing (only once on mount)
	React.useEffect(() => {
		if (defaultValues?.startDate && defaultValues?.endDate) {
			const startDate = new Date(defaultValues.startDate)
				.toISOString()
				.split('T')[0]
			const endDate = new Date(defaultValues.endDate)
				.toISOString()
				.split('T')[0]
			setDateFilter({
				startDate,
				endDate,
			})
		}
	}, [])

	const form = useForm<DepositPromotionFormValues>({
		resolver: zodResolver(depositPromotionFormSchema),
		defaultValues: {
			name: '',
			banner: '',
			shortDescription: '',
			description: '',
			order: undefined,
			minimumRankOrder: 0,
			isActive: true,
			isForPlayer: true,
			isForGuestOnly: false,
			isCoupon: false,
			requireConnectedLine: false,
			allowInstantClaim: false,
			allowedTagIds: defaultValues?.allowedTagIds || [],
			creditType: CreditType.FIXED,
			gameTypes: [],
			whitelistGameProviderIds: defaultValues?.whitelistGameProviderIds || [],
			claimConstraint: [],
			walletCondition: {
				pgHard:
					defaultValues?.walletCondition?.pgHard ??
					(defaultValues as any)?.pgHard ??
					false,
				jokerHard:
					defaultValues?.walletCondition?.jokerHard ??
					(defaultValues as any)?.jokerHard ??
					false,
				jiliHard:
					defaultValues?.walletCondition?.jiliHard ??
					(defaultValues as any)?.jiliHard ??
					false,
				ppHard:
					defaultValues?.walletCondition?.ppHard ??
					(defaultValues as any)?.ppHard ??
					false,
				nlcHard:
					defaultValues?.walletCondition?.nlcHard ??
					(defaultValues as any)?.nlcHard ??
					false,
			},
			turnoverRound: undefined,
			fixedDepositAmount: undefined,
			minimumWithdrawCredit: undefined,
			maximumWithdrawCredit: undefined,
			maximumWithdrawMultiplierOfDeposit: undefined,
			maximumCurrentCredit: undefined,
			rankExperiencePoint: undefined,
			limitQuota: undefined,
			perPlayerQuota: undefined,
			limitQuotaPerDay: undefined,
			limitQuotaPerMonth: undefined,
			limitQuotaPerDayPerPlayer: undefined,
			hasExpiration:
				defaultValues?.hasExpiration ||
				!!(defaultValues?.startDate && defaultValues?.endDate),
			usePool: false,
			poolPresetConfigs: defaultValues?.poolPresetConfigs || [
				{
					rewardSize: undefined,
					rewardRatio: undefined,
				},
			],
			presetConfigs: [
				{
					thresholdType:
						defaultValues?.presetConfigs?.[0]?.thresholdType || 'FIXED',
					turnoverThreshold:
						defaultValues?.presetConfigs?.[0]?.turnoverThreshold || undefined,
					turnoverMultiplier:
						defaultValues?.presetConfigs?.[0]?.turnoverMultiplier || undefined,
					presetId: defaultValues?.presetConfigs?.[0]?.presetId || undefined,
					presetNumber:
						defaultValues?.presetConfigs?.[0]?.presetNumber || undefined,
				},
			],
			...defaultValues,
		},
	})

	const { data: gameProviders } = useQueryGetGameProviderList({
		take: 1000,
		skip: 0,
	})

	const handleSubmit = (values: DepositPromotionFormValues) => {
		const {
			hasExpiration,
			poolPresetConfigs,
			presetConfigs,
			walletCondition,
			...restValues
		} = values

		const payload = {
			...restValues,
			// Flatten walletCondition fields to root level for backward compatibility
			pgHard: walletCondition?.pgHard,
			jokerHard: walletCondition?.jokerHard,
			jiliHard: walletCondition?.jiliHard,
			ppHard: walletCondition?.ppHard,
			nlcHard: walletCondition?.nlcHard,
			startDate: hasExpiration ? dateFilter.startDate : null,
			endDate: hasExpiration ? dateFilter.endDate : null,
			presetConfigs: (presetConfigs ?? []).filter((config) => {
				const hasThreshold =
					config.thresholdType === 'MULTIPLIER'
						? config.turnoverMultiplier != null
						: config.turnoverThreshold != null
				return hasThreshold && config.presetId && config.presetNumber != null
			}),
			...(values.usePool ? { poolPresetConfigs } : {}),
		} as unknown as CreateDepositPromotionRequest

		onSubmit(payload)
	}

	const { mutateAsync: getDepositBannerFileUrl } =
		useMutationCreatePromotionBannerFileUrl()

	const creditType = form.watch('creditType')

	return (
		<FormProvider {...form}>
			<form
				onSubmit={(e) => {
					e.preventDefault()
					const result = form.handleSubmit((values) => {
						handleSubmit(values)
					}, showFormValidationToast)
					result(e)
				}}
				className="space-y-4 sm:space-y-6"
			>
				{/* RTP Warning Banner */}
				<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
					<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
					<div className="flex-1 text-amber-800 dark:text-amber-200">
						<p className="font-medium">โปรดตั้งค่า RTP ในทุกโปรโมชั่น</p>
						<p className="mt-0.5">
							โปรโมชั่นที่ให้โบนัสหรือเครดิตฟรีสูงควรมีการตั้งค่า RTP
							ที่เหมาะสมเสมอ เพื่อป้องกันการขาดทุนในระยะยาว{' '}
							<strong>
								และต้องแน่ใจว่าค่ายเกมที่เลือกรองรับระบบปรับแตก
								มิฉะนั้นการตั้งค่า RTP จะไม่มีผล และทำให้เราขาดทุนได้
							</strong>
						</p>
						<div className="mt-3">
							<RtpGuideButton />
						</div>
					</div>
				</div>

				{/* Basic Information and Promotion Type Section */}
				<div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
					{/* Left Column - Basic Information and Time Period */}
					<div className="space-y-3 lg:space-y-4">
						{/* Basic Information Section */}
						<div className="space-y-4 rounded-lg border p-4 sm:p-6">
							<h2 className="text-base font-semibold sm:text-lg">
								ข้อมูลทั่วไป
							</h2>
							<div className="space-y-3 sm:space-y-4">
								<FormField
									control={form.control}
									name="name"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm sm:text-base">
												<RequiredIndicator /> ตั้งชื่อโปรโมชั่น
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder="กรอกชื่อโปรโมชั่น"
													className="text-sm sm:text-base"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="order"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm sm:text-base">
												<RequiredIndicator /> ลำดับ
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													min={0}
													onKeyDown={preventMinusInput}
													placeholder="กรอกลำดับ"
													{...field}
													value={field.value ?? ''}
													onChange={(e) =>
														field.onChange(
															e.target.value ? Number(e.target.value) : null,
														)
													}
													className="text-sm sm:text-base"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>

						{/* Time Period Section */}
						<div className="space-y-4 rounded-lg border p-4 sm:p-6">
							<h2 className="text-base font-semibold sm:text-lg">ระยะเวลา</h2>
							<div className="space-y-3 sm:space-y-4">
								<FormField
									control={form.control}
									name="hasExpiration"
									render={({ field }) => (
										<FormItem className="space-y-3">
											<FormLabel className="text-sm sm:text-base">
												ระยะเวลาโปรโมชั่น
											</FormLabel>
											<FormControl>
												<RadioGroup
													onValueChange={(value) =>
														field.onChange(value === 'true')
													}
													value={field.value ? 'true' : 'false'}
													className="flex flex-col space-y-1"
												>
													<FormItem className="flex items-center space-x-3 space-y-0">
														<FormControl>
															<RadioGroupItem value="false" />
														</FormControl>
														<FormLabel className="text-sm font-normal sm:text-base">
															ไม่มีกำหนด
														</FormLabel>
													</FormItem>
													<FormItem className="flex items-center space-x-3 space-y-0">
														<FormControl>
															<RadioGroupItem value="true" />
														</FormControl>
														<FormLabel className="text-sm font-normal sm:text-base">
															เลือกระยะเวลา
														</FormLabel>
													</FormItem>
												</RadioGroup>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								{form.watch('hasExpiration') && <DateRangePicker />}
							</div>
						</div>
					</div>

					{/* Right Column - Promotion Type Section */}
					<div className="space-y-4 rounded-lg border p-4 sm:p-6">
						<h2 className="text-base font-semibold sm:text-lg">
							{' '}
							<RequiredIndicator /> ประเภทโปรโมชั่น
						</h2>
						<div className="space-y-3 sm:space-y-4">
							<FormField
								control={form.control}
								name="isForPlayer"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3 sm:p-4">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={(checked) => {
													field.onChange(checked)
													if (checked) {
														form.setValue('isForGuestOnly', false)
														form.setValue('isCoupon', false)
													}
												}}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel className="text-sm sm:text-base">
												โปรสำหรับผู้เล่น
											</FormLabel>
											<p className="text-xs text-muted-foreground sm:text-sm">
												เลือกว่าโปรโมชั่นนี้สำหรับผู้เล่นหรือไม่
											</p>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="isCoupon"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3 sm:p-4">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={(checked) => {
													field.onChange(checked)
													if (checked) {
														form.setValue('isForPlayer', false)
														form.setValue('isForGuestOnly', false)
													}
												}}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel className="text-sm sm:text-base">
												โปรโมชั่นพิเศษ (คูปอง/แคมเปญ)
											</FormLabel>
											<p className="text-xs text-muted-foreground sm:text-sm">
												ซ่อนจากรายการทั่วไป - ใช้สำหรับคูปอง, หรือแคมเปญพิเศษ
											</p>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="isForGuestOnly"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3 sm:p-4">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={(checked) => {
													field.onChange(checked)
													if (checked) {
														form.setValue('isForPlayer', false)
														form.setValue('isCoupon', false)
													}
												}}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel className="text-sm sm:text-base">
												โปรสำหรับผู้เล่นทดลอง
											</FormLabel>
											<p className="text-xs text-muted-foreground sm:text-sm">
												เลือกว่าโปรโมชั่นนี้สำหรับผู้เล่นทดลองหรือไม่
											</p>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="requireConnectedLine"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3 sm:p-4">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel className="text-sm sm:text-base">
												ต้องเชื่อมต่อ LINE
											</FormLabel>
											<p className="text-xs text-muted-foreground sm:text-sm">
												เลือกว่าโปรโมชั่นนี้ต้องเชื่อมต่อ LINE หรือไม่
											</p>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="allowInstantClaim"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3 sm:p-4">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel className="text-sm sm:text-base">
												รับเครดิตฟรีทันที (ไม่ต้องฝาก)
											</FormLabel>
											<p className="text-xs text-muted-foreground sm:text-sm">
												อนุญาตให้ส่งโปรโมชั่นนี้ผ่าน LINE
												พร้อมลิงก์รับเครดิตฟรีได้ทันทีโดยไม่ต้องฝากเงิน
											</p>
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				{/* Allowed Customer Tags and Rank Experience Section */}
				<div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
					{/* Allowed Customer Tags Section */}
					<div className="space-y-4 rounded-lg border p-4 sm:p-6">
						<h2 className="text-base font-semibold sm:text-lg">
							แท็กลูกค้าที่อนุญาต
						</h2>
						<div className="space-y-3 sm:space-y-4">
							<FormField
								control={form.control}
								name="allowedTagIds"
								render={({ field }) => {
									const selectedTags =
										allTags?.filter((tag) => field.value?.includes(tag.id)) ||
										[]

									return (
										<FormItem>
											<FormDescription>
												ระบุแท็กลูกค้าที่สามารถรับโปรโมชั่นนี้ได้
												(ถ้าไม่ระบุจะอนุญาตทุกคน)
											</FormDescription>
											<div className="space-y-4">
												{/* Search Input */}
												{!isLoadingTags && allTags && allTags.length > 0 && (
													<div className="relative">
														<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
														<Input
															type="text"
															placeholder="ค้นหาแท็ก..."
															value={tagSearchQuery}
															onChange={(e) =>
																setTagSearchQuery(e.target.value)
															}
															className="pl-9"
														/>
														{tagSearchQuery && (
															<button
																type="button"
																onClick={() => setTagSearchQuery('')}
																className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
															>
																<X className="h-4 w-4" />
															</button>
														)}
													</div>
												)}

												{/* Tag Selection with Checkboxes */}
												{isLoadingTags ? (
													<p className="text-sm text-muted-foreground">
														กำลังโหลดแท็ก...
													</p>
												) : allTags && allTags.length > 0 ? (
													<>
														{filteredTags.length > 0 ? (
															<div className="max-h-[300px] space-y-2 overflow-y-auto rounded-lg border p-4">
																{filteredTags.map((tag) => {
																	const isSelected = field.value?.includes(
																		tag.id,
																	)

																	return (
																		<div
																			key={tag.id}
																			className="flex items-center space-x-3 rounded-md p-2 hover:bg-muted/50"
																		>
																			<Checkbox
																				id={`tag-${tag.id}`}
																				checked={isSelected}
																				onCheckedChange={() => {
																					const currentValue = field.value || []
																					const newValue =
																						currentValue.includes(tag.id)
																							? currentValue.filter(
																									(id) => id !== tag.id,
																								)
																							: [...currentValue, tag.id]
																					field.onChange(newValue)
																				}}
																			/>
																			<label
																				htmlFor={`tag-${tag.id}`}
																				className="flex flex-1 cursor-pointer items-center"
																			>
																				<Badge
																					variant="outline"
																					style={{ backgroundColor: tag.color }}
																					className="text-white"
																				>
																					{tag.name}
																				</Badge>
																			</label>
																		</div>
																	)
																})}
															</div>
														) : (
															<div className="rounded-lg border p-4 text-center">
																<p className="text-sm text-muted-foreground">
																	ไม่พบแท็กที่ตรงกับการค้นหา &quot;
																	{tagSearchQuery}&quot;
																</p>
															</div>
														)}
													</>
												) : (
													<p className="text-sm text-muted-foreground">
														ไม่มีแท็กในระบบ
													</p>
												)}

												{/* Selected Tags Preview */}
												{selectedTags.length > 0 && (
													<div>
														<p className="mb-2 text-sm font-medium">
															แท็กที่เลือก ({selectedTags.length})
														</p>
														<div className="flex flex-wrap gap-2">
															{selectedTags.map((tag) => (
																<Badge
																	key={tag.id}
																	variant="outline"
																	style={{ backgroundColor: tag.color }}
																	className="text-white"
																>
																	{tag.name}
																	<button
																		type="button"
																		className="ml-2 hover:opacity-70"
																		onClick={() => {
																			const newValue = field.value?.filter(
																				(id) => id !== tag.id,
																			)
																			field.onChange(newValue)
																		}}
																	>
																		<X className="h-3 w-3" />
																	</button>
																</Badge>
															))}
														</div>

														<Button
															type="button"
															variant="ghost"
															size="sm"
															onClick={() => {
																field.onChange([])
															}}
															className="mt-2"
														>
															ล้างทั้งหมด
														</Button>
													</div>
												)}
											</div>
											<FormMessage />
										</FormItem>
									)
								}}
							/>
						</div>
					</div>

					{/* Rank Experience Point Section */}
					<div className="space-y-4 rounded-lg border p-4 sm:p-6">
						<h2 className="text-base font-semibold sm:text-lg">
							คะแนนประสบการณ์
						</h2>
						<div className="space-y-3 sm:space-y-4">
							<FormField
								control={form.control}
								name="rankExperiencePoint"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											EXP แรงค์ได้หลังจากรับโปรโมชั่น
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												min={0}
												onKeyDown={preventMinusInput}
												placeholder="กรอกจำนวนประสบการณ์"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				{/* Promotion Constraints and Game Settings Section */}
				<div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
					{/* Promotion Constraints Section */}
					<div className="space-y-4 rounded-lg border p-4 sm:p-6">
						<h2 className="text-base font-semibold sm:text-lg">
							เงื่อนไขการรับโปรโมชั่น
						</h2>
						<ConstraintSelector
							control={form.control}
							name="claimConstraint"
							type="deposit"
						/>

						<FormField
							control={form.control}
							name="minimumRankOrder"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-sm sm:text-base">
										แรงค์ขั้นต่ำที่สามารถรับโปรโมชั่นได้
										(ไม่เลือกเท่ากับใช้ได้กับทุกแรงค์)
									</FormLabel>
									<Select
										onValueChange={(value) => field.onChange(Number(value))}
										value={field.value ? String(field.value) : '0'}
									>
										<FormControl>
											<SelectTrigger className="text-sm sm:text-base">
												<SelectValue placeholder="เลือกแรงค์ขั้นต่ำ" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value="0">ใช้ได้กับทุกแรงค์</SelectItem>
											{rankOptions.map((rank) => (
												<SelectItem key={rank.value} value={String(rank.value)}>
													{rank.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					{/* Game Settings Section */}
					<div className="space-y-4 rounded-lg border p-4 sm:p-6">
						<h2 className="text-base font-semibold sm:text-lg">
							การตั้งค่าเกม
						</h2>
						<FormField
							control={form.control}
							name="gameTypes"
							render={() => (
								<FormItem>
									<FormLabel className="text-sm sm:text-base">
										ประเภทเกมที่เข้าร่วมโปรโมชั่น (ไม่เลือกเท่ากับอนุญาตทั้งหมด)
									</FormLabel>
									<div className="flex flex-wrap gap-2 sm:gap-3">
										{[
											GameType.SLOT,
											GameType.FISHING,
											GameType.CASINO,
											GameType.LOTTO,
											GameType.SPORT,
										].map((type) => (
											<FormField
												key={type}
												control={form.control}
												name="gameTypes"
												render={({ field }) => (
													<FormItem className="flex items-center gap-2">
														<FormControl>
															<Checkbox
																checked={field.value?.includes(type as any)}
																onCheckedChange={(checked) => {
																	const updatedValue = checked
																		? [...(field?.value || []), type]
																		: field.value?.filter(
																				(value: GameType) => value !== type,
																			)
																	field.onChange(updatedValue)
																}}
															/>
														</FormControl>
														<FormLabel className="text-sm sm:text-base">
															{type}
														</FormLabel>
													</FormItem>
												)}
											/>
										))}
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="whitelistGameProviderIds"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-sm sm:text-base">
										ผู้ให้บริการเกมที่ร่วมในโปรโมชั่น
										(ไม่เลือกเท่ากับอนุญาตทั้งหมด)
									</FormLabel>
									<FormControl>
										<GameProviderSelector
											field={field}
											gameProviders={gameProviders?.data}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Separator />

						{/* Provider Version Settings */}
						<ProviderVersionSettings
							control={form.control}
							fieldPrefix="walletCondition"
						/>
					</div>
				</div>

				{/* Credit Settings and Withdrawal Limits Section */}
				<div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
					{/* Credit Settings Section */}
					<div className="space-y-4 rounded-lg border p-4 sm:p-6">
						<h2 className="text-base font-semibold sm:text-lg">
							การตั้งค่าเครดิต
						</h2>
						<div className="space-y-3 sm:space-y-4">
							<FormField
								control={form.control}
								name="creditType"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											ประเภทการจ่ายเครดิต
										</FormLabel>
										<Select
											onValueChange={field.onChange}
											defaultValue={field.value}
										>
											<FormControl>
												<SelectTrigger className="text-sm sm:text-base">
													<SelectValue placeholder="Select credit type" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												<SelectItem value="FIXED">
													Fixed (ระบุจำนวนเครดิต)
												</SelectItem>
												<SelectItem value="RATIO">
													Ratio (เปอร์เซ็นต์ตามยอดฝาก)
												</SelectItem>
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							{creditType === 'FIXED' ? (
								<FormField
									control={form.control}
									name="creditAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel required className="text-sm sm:text-base">
												จำนวนเครดิตโบนัส
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="กรอกจำนวนเครดิต"
													min={0}
													onKeyDown={preventMinusInput}
													{...field}
													value={field.value ?? ''}
													onChange={(e) =>
														field.onChange(
															e.target.value ? Number(e.target.value) : null,
														)
													}
													className="text-sm sm:text-base"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							) : (
								<>
									<FormField
										control={form.control}
										name="creditRatio"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm sm:text-base">
													<RequiredIndicator />
													เปอร์เซ็นต์จ่ายเครดิต
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														step="0.01"
														min={0}
														placeholder="กรอกเปอร์เซ็นต์จ่ายเครดิต"
														onKeyDown={preventMinusInput}
														value={
															field.value !== null && field.value !== undefined
																? String(Number((field.value * 100).toFixed(4)))
																: ''
														}
														onChange={(e) => {
															const value = e.target.value
																? parseFloat(e.target.value)
																: null
															field.onChange(
																value !== null ? value / 100 : null,
															)
														}}
														className="text-sm sm:text-base"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="maxCreditAmount"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm sm:text-base">
													จำนวนจ่ายเครดิตสูงสุด (ไม่ใส่เท่ากับไม่จำกัด)
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder="กรอกจำนวนจ่ายเครดิตสูงสุด"
														min={0}
														onKeyDown={preventMinusInput}
														{...field}
														value={field.value ?? ''}
														onChange={(e) =>
															field.onChange(
																e.target.value ? Number(e.target.value) : null,
															)
														}
														className="text-sm sm:text-base"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</>
							)}

							<FormField
								control={form.control}
								name="turnoverRound"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											<RequiredIndicator /> เทิร์นโอเวอร์
											(จำนวนเท่าของยอดฝากรวมโบนัส)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												onKeyDown={preventMinusInput}
												placeholder="กรอกเทิร์นโอเวอร์ (จำนวนเท่าของยอดฝากรวมโบนัส)"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="fixedDepositAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำกัดจำนวนเงินฝาก (ไม่ใส่เท่ากับไม่จำกัด)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												min={0}
												onKeyDown={preventMinusInput}
												placeholder="กรอกจำนวนเงินฝาก"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="diamondAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนเพชรที่ได้รับ (ไม่ใส่เท่ากับไม่ได้รับเพชร)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												min={0}
												step={1}
												onKeyDown={preventMinusInput}
												placeholder="กรอกจำนวนเพชร"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value
															? Math.floor(Number(e.target.value))
															: null,
													)
												}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{/* Withdrawal Limits Section */}
					<div className="space-y-4 rounded-lg border p-4 sm:p-6">
						<h2 className="text-base font-semibold sm:text-lg">
							ข้อจำกัดการถอน
						</h2>
						<div className="space-y-3 sm:space-y-4">
							<FormField
								control={form.control}
								name="minimumWithdrawCredit"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนการถอนขั้นต่ำ
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกจำนวนการถอนขั้นต่ำ"
												onKeyDown={preventMinusInput}
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="maximumWithdrawCredit"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนการถอนสูงสุด
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												onKeyDown={preventMinusInput}
												placeholder="กรอกจำนวนการถอนสูงสุด"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="maximumWithdrawMultiplierOfDeposit"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											ถอนได้เป็นกี่เท่าของการฝาก ไม่รวมโบนัส
											(ไม่ใส่เท่ากับไม่จำกัด)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												min={0}
												onKeyDown={preventMinusInput}
												placeholder="เช่น 5 = ถอนสูงสุด 5 เท่าของยอดฝากตั้งต้น ไม่รวมโบนัส"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="maximumCurrentCredit"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											เครดิตคงเหลือสูงสุดก่อนรับโปรนี้ (ไม่ใส่เท่ากับไม่จำกัด)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												onKeyDown={preventMinusInput}
												placeholder="กรอกเครดิตคงเหลือสูงสุดก่อนรับโปรนี้"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				{/* Quota Limits Section */}
				<div className="space-y-4 rounded-lg border p-4 sm:p-6">
					<h2 className="text-base font-semibold sm:text-lg">ข้อจำกัดโควตา</h2>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 lg:gap-4">
						<FormField
							control={form.control}
							name="limitQuota"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-sm sm:text-base">
										จำกัดจำนวนโควตาของทั้งระบบ (ไม่ใส่เท่ากับไม่จำกัด)
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											onKeyDown={preventMinusInput}
											placeholder="กรอกจำนวนโควตา"
											{...field}
											value={field.value ?? ''}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											className="text-sm sm:text-base"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="perPlayerQuota"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-sm sm:text-base">
										จำกัดจำนวนโควตาต่อผู้เล่น (ไม่ใส่เท่ากับไม่จำกัด)
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											onKeyDown={preventMinusInput}
											placeholder="กรอกจำนวนโควตาต่อผู้เล่น"
											{...field}
											value={field.value ?? ''}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											className="text-sm sm:text-base"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="limitQuotaPerDay"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-sm sm:text-base">
										จำกัดจำนวนโควตาต่อวัน (ไม่ใส่เท่ากับไม่จำกัด)
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											onKeyDown={preventMinusInput}
											placeholder="กรอกจำนวนโควตาต่อวัน"
											{...field}
											value={field.value ?? ''}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											className="text-sm sm:text-base"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="limitQuotaPerMonth"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-sm sm:text-base">
										จำกัดจำนวนโควตาต่อเดือน (ไม่ใส่เท่ากับไม่จำกัด)
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											onKeyDown={preventMinusInput}
											placeholder="กรอกจำนวนโควตาต่อเดือน"
											{...field}
											value={field.value ?? ''}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											className="text-sm sm:text-base"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="limitQuotaPerDayPerPlayer"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-sm sm:text-base">
										จำกัดจำนวนโควตาต่อวันต่อผู้เล่น (ไม่ใส่เท่ากับไม่จำกัด)
									</FormLabel>
									<FormControl>
										<Input
											type="number"
											min={0}
											onKeyDown={preventMinusInput}
											placeholder="กรอกจำนวนโควตาต่อวันต่อผู้เล่น"
											{...field}
											value={field.value ?? ''}
											onChange={(e) =>
												field.onChange(
													e.target.value ? Number(e.target.value) : null,
												)
											}
											className="text-sm sm:text-base"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				{/* Pool and Preset Configuration Section */}
				{/* Pool and Preset Configuration Section */}
				{import.meta.env.VITE_DISABLED_PG_FEATURES !== 'true' && (
					<div className="space-y-4 rounded-lg border p-4 sm:space-y-6 sm:p-6">
						<div className="space-y-2">
							<h2 className="text-lg font-semibold sm:text-xl">
								การตั้งค่า RTP
							</h2>
							<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
								<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
								<div className="flex-1 text-amber-800 dark:text-amber-200">
									<p className="font-medium">โปรดตั้งค่า RTP ในทุกโปรโมชั่น</p>
									<p className="mt-0.5">
										โปรโมชั่นหรือกิจกรรมที่ให้โบนัสหรือเครดิตฟรีสูงควรตั้งค่า
										RTP ต่ำ (กินเยอะ) เพื่อให้ผู้เล่นเครดิตน้อยกว่าเงินที่ฝากมา
										หลังทำการเคลียร์เทิร์นครบ หากตั้งค่า RTP สูงเกินไป
										ระบบจะขาดทุนในระยะยาวเนื่องจากต้นทุนสูงกว่ารายได้จากการกินผู้เล่น{' '}
										<strong>
											และต้องแน่ใจว่าค่ายเกมที่เลือกรองรับระบบปรับแตก
											มิฉะนั้นการตั้งค่า RTP จะไม่มีผล และทำให้เราขาดทุนได้
										</strong>
									</p>
									<div className="mt-3">
										<RtpGuideButton />
									</div>
								</div>
							</div>
						</div>
						<div className="space-y-4 sm:space-y-6">
							{/* Pool Section - Commented out */}
							{/* <FormField
							control={form.control}
							name="usePool"
							render={({ field }) => (
								<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3 sm:p-4">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
											disabled={!isHardProviderSelected}
										/>
									</FormControl>
									<div className="space-y-1 leading-none">
										<FormLabel className="text-sm sm:text-base">
											ใช้ Pool
										</FormLabel>
										<p className="text-xs text-muted-foreground sm:text-sm">
											เลือกว่าจะใช้ Pool หรือไม่
										</p>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						{form.watch('usePool') && (
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="poolPresetConfigs.0.rewardSize"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm sm:text-base">
												<RequiredIndicator /> เงินรางวัลเริ่มต้น
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													min={0}
													onKeyDown={preventMinusInput}
													placeholder="กรอกเงินรางวัลเริ่มต้น"
													{...field}
													value={field.value ?? ''}
													onChange={(e) =>
														field.onChange(
															e.target.value ? Number(e.target.value) : null,
														)
													}
													disabled={!isHardProviderSelected}
													className="text-sm sm:text-base"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="poolPresetConfigs.0.rewardRatio"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm sm:text-base">
												<RequiredIndicator /> เปอร์เซ็นต์เงินรางวัลลูกค้า
												(0-100%)
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													step="0.01"
													min={0}
													max={100}
													onKeyDown={preventMinusInput}
													placeholder="กรอกเปอร์เซ็นต์เงินรางวัลลูกค้า (0-100%)"
													value={
														field.value !== null && field.value !== undefined
															? String(Number((field.value * 100).toFixed(4)))
															: ''
													}
													onChange={(e) => {
														const value = e.target.value
															? parseFloat(e.target.value)
															: null
														field.onChange(
															value !== null ? value / 100 : null,
														)
													}}
													disabled={!isHardProviderSelected}
													className="text-sm sm:text-base"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						)}
						<Separator /> */}

							{/* Preset Section - Still Active */}
							{/* Preset Section - Still Active */}
							<FormField
								control={form.control}
								name="presetConfigs"
								render={({ field }) => (
									<FormItem>
										<div className="flex items-center gap-2">
											<FormLabel className="text-sm text-foreground sm:text-base">
												ตั้งค่า RTP Preset
											</FormLabel>
											<RtpPresetInfoTooltip />
										</div>
										<PresetPriorityHint
											where="promotion"
											className="mt-2 w-full"
										/>
										<div className="mb-2 text-sm text-muted-foreground">
											หมายเหตุ: ระบบมี 2 Preset ที่สามารถตั้งค่าได้
										</div>

										{/* Global Threshold Type Toggle */}
										<div className="mb-4">
											<FormLabel className="text-sm text-foreground sm:text-base">
												ประเภทเงื่อนไข
											</FormLabel>
											<div className="mt-1 flex gap-2">
												<Button
													type="button"
													size="sm"
													variant={
														presetThresholdType !== 'MULTIPLIER'
															? 'default'
															: 'outline'
													}
													onClick={() => {
														setPresetThresholdType('FIXED')
														field.onChange(
															(field.value || []).map((c) => ({
																...c,
																thresholdType: 'FIXED' as const,
																turnoverMultiplier: null,
															})),
														)
													}}
												>
													คงที่ (เครดิต)
												</Button>
												<Button
													type="button"
													size="sm"
													variant={
														presetThresholdType === 'MULTIPLIER'
															? 'default'
															: 'outline'
													}
													onClick={() => {
														setPresetThresholdType('MULTIPLIER')
														field.onChange(
															(field.value || []).map((c) => ({
																...c,
																thresholdType: 'MULTIPLIER' as const,
																turnoverThreshold: null,
															})),
														)
													}}
												>
													เท่าของยอดฝากรวมโบนัส (x)
												</Button>
											</div>
											<p className="mt-2 text-xs text-muted-foreground">
												หมายเหตุ: ระบุจำนวนเทิร์นโอเวอร์เป็นตัวเลขเครดิต (เช่น
												เมื่อผู้เล่นทำเทิร์นครบ 500 เครดิต) หรือ
												ระบุจำนวนเทิร์นโอเวอร์เป็นจำนวนเท่าของยอดฝากรวมโบนัส
												(เช่น เมื่อผู้เล่นทำเทิร์นครบ 1 เท่า)
											</p>
										</div>

										<FormControl>
											<div className="space-y-6">
												{field.value?.map((config, index) => {
													const isMultiplier =
														presetThresholdType === 'MULTIPLIER'
													const turnoverRoundValue = form.watch('turnoverRound')
													const multiplierExceedsRound =
														isMultiplier &&
														turnoverRoundValue != null &&
														config.turnoverMultiplier != null &&
														config.turnoverMultiplier >= turnoverRoundValue

													return (
														<div
															key={index}
															className="space-y-4 rounded-lg border p-4"
														>
															{/* Preset Number Header */}
															<div className="flex items-center justify-between">
																<h3 className="text-base font-semibold">
																	Preset {index + 1}
																</h3>
																<Button
																	type="button"
																	variant="destructive"
																	size="sm"
																	onClick={() => {
																		const newValue = field.value?.filter(
																			(_, i) => i !== index,
																		)
																		field.onChange(newValue)
																	}}
																>
																	ลบ
																</Button>
															</div>

															{/* Turnover Threshold (FIXED) */}
															{!isMultiplier && (
																<div className="grid grid-cols-1 gap-4">
																	<FormItem>
																		<FormLabel className="text-sm text-foreground sm:text-base">
																			จำนวนเทิร์นโอเวอร์
																			(จำนวนเทิร์นโอเวอร์ที่จะเริ่มใช้งาน Preset
																			นี้)
																		</FormLabel>
																		<FormControl>
																			<Input
																				type="number"
																				min={0}
																				placeholder="จำนวนเทิร์นโอเวอร์"
																				value={config.turnoverThreshold ?? ''}
																				onChange={(e) => {
																					const newValue = [
																						...(field.value || []),
																					]
																					newValue[index] = {
																						...newValue[index],
																						turnoverThreshold: e.target.value
																							? Number(e.target.value)
																							: null,
																					}
																					field.onChange(newValue)
																				}}
																				className="text-sm sm:text-base"
																			/>
																		</FormControl>
																	</FormItem>
																</div>
															)}

															{/* Turnover Multiplier (MULTIPLIER) */}
															{isMultiplier && (
																<div className="grid grid-cols-1 gap-4">
																	<FormItem>
																		<FormLabel className="text-sm text-foreground sm:text-base">
																			จำนวนเท่าของยอดฝากรวมโบนัส
																			{turnoverRoundValue != null && (
																				<span className="ml-1 text-xs font-normal text-muted-foreground">
																					(ต้องน้อยกว่า {turnoverRoundValue}x)
																				</span>
																			)}
																		</FormLabel>
																		<FormControl>
																			<Input
																				type="number"
																				min={0}
																				step={0.5}
																				placeholder="เช่น 1, 2, 3"
																				value={config.turnoverMultiplier ?? ''}
																				onChange={(e) => {
																					const newValue = [
																						...(field.value || []),
																					]
																					newValue[index] = {
																						...newValue[index],
																						turnoverMultiplier: e.target.value
																							? Number(e.target.value)
																							: null,
																					}
																					field.onChange(newValue)
																				}}
																				className="text-sm sm:text-base"
																			/>
																		</FormControl>
																		{multiplierExceedsRound && (
																			<div className="mt-1 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs dark:border-amber-800 dark:bg-amber-950/30">
																				<TriangleAlert className="mt-0.5 h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
																				<span className="text-amber-800 dark:text-amber-200">
																					ค่า {config.turnoverMultiplier}x
																					มากกว่า เทิร์นโอเวอร์ของโปรโมชั่น (
																					{turnoverRoundValue}x)
																					หากเทิร์นโอเวอร์ถึงค่านี้แล้ว
																					ผู้เล่นจะสำเร็จและ Preset
																					นี้จะไม่ถูกใช้งาน
																				</span>
																			</div>
																		)}
																	</FormItem>
																</div>
															)}

															{/* RTP Preset Dropdown (PG/Joker/Jili) */}
															<div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/30">
																<p className="mb-2 text-xs font-medium text-blue-700 dark:text-blue-300">
																	🎮 ใช้กับ: PGHARD, JOKERHARD, JILIHARD
																</p>
																<p className="mb-3 text-xs text-blue-600 dark:text-blue-400">
																	ค่านี้จะถูกใช้เมื่อผู้เล่นเล่นเกมจากผู้ให้บริการ
																	PGHARD, JOKERHARD หรือ JILIHARD เท่านั้น
																</p>
																<FormItem>
																	<FormLabel className="text-sm text-foreground sm:text-base">
																		เลือก RTP Preset
																	</FormLabel>
																	<FormControl>
																		<Select
																			value={config.presetId ?? undefined}
																			onValueChange={(value) => {
																				const newValue = [
																					...(field.value || []),
																				]
																				newValue[index] = {
																					...newValue[index],
																					presetId: value,
																				}
																				field.onChange(newValue)
																			}}
																		>
																			<SelectTrigger className="text-sm sm:text-base">
																				<SelectValue placeholder="เลือก RTP Preset" />
																			</SelectTrigger>
																			<SelectContent>
																				{presets?.presets.map((preset) => (
																					<SelectItem
																						key={preset.id}
																						value={preset.id}
																					>
																						{preset.note}
																					</SelectItem>
																				))}
																			</SelectContent>
																		</Select>
																	</FormControl>
																	{}
																	{(
																		form.formState.errors.presetConfigs as any
																	)?.[index]?.presetId?.message && (
																		<p className="text-sm font-medium text-destructive">
																			{
																				(
																					form.formState.errors
																						.presetConfigs as any
																				)?.[index]?.presetId?.message
																			}
																		</p>
																	)}
																</FormItem>
															</div>

															{/* RTP Percentage Number (HardV2) */}
															<div className="rounded-md border border-purple-200 bg-purple-50 p-3 dark:border-purple-800 dark:bg-purple-950/30">
																<p className="mb-2 text-xs font-medium text-purple-700 dark:text-purple-300">
																	🎮 ใช้กับ: ค่ายปรับแตกอื่นๆทั้งหมด
																</p>
																<p className="mb-3 text-xs text-purple-600 dark:text-purple-400">
																	ค่านี้จะถูกใช้เมื่อผู้เล่นเล่นเกมจากผู้ให้บริการ
																	PPHARD, NLCHARD และค่ายอื่นๆทั้งหมด
																</p>
																<FormItem>
																	<FormLabel className="text-sm text-foreground sm:text-base">
																		เปอร์เซ็น RTP (30-99)
																	</FormLabel>
																	<FormControl>
																		<Input
																			type="number"
																			min={30}
																			max={99}
																			placeholder="ระบุเปอร์เซ็น RTP (30-99)"
																			value={config.presetNumber ?? ''}
																			onChange={(e) => {
																				const newValue = [
																					...(field.value || []),
																				]
																				newValue[index] = {
																					...newValue[index],
																					presetNumber: e.target.value
																						? Number(e.target.value)
																						: null,
																				}
																				field.onChange(newValue)
																			}}
																			className="text-sm sm:text-base"
																		/>
																	</FormControl>
																	{}
																	{(
																		form.formState.errors.presetConfigs as any
																	)?.[index]?.presetNumber?.message && (
																		<p className="text-sm font-medium text-destructive">
																			{
																				(
																					form.formState.errors
																						.presetConfigs as any
																				)?.[index]?.presetNumber?.message
																			}
																		</p>
																	)}
																</FormItem>
															</div>
														</div>
													)
												})}
												<Button
													type="button"
													variant="outline"
													size="sm"
													className="w-full sm:w-auto"
													onClick={() => {
														const newValue = [
															...(field.value || []),
															{
																thresholdType: presetThresholdType,
																turnoverThreshold: undefined,
																turnoverMultiplier: undefined,
																presetId: presets?.presets[0]?.id,
																order: field.value?.length || 0,
															},
														]
														field.onChange(newValue)
													}}
												>
													เพิ่ม Preset
												</Button>
											</div>
										</FormControl>
										{typeof form.formState.errors.presetConfigs?.message ===
											'string' && (
											<p className="text-sm font-medium text-destructive">
												{form.formState.errors.presetConfigs.message}
											</p>
										)}
									</FormItem>
								)}
							/>
						</div>
					</div>
				)}

				{/* Media Section */}
				{form.watch('isForPlayer') && (
					<div className="space-y-4 rounded-lg border p-4 sm:p-6">
						<h2 className="text-base font-semibold sm:text-lg">
							สื่อและรายละเอียด
						</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="banner"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											Banner URL{' '}
											<span className="text-amber-600 dark:text-amber-400">
												(สัดส่วน 16:9)
											</span>
										</FormLabel>
										<FormControl>
											<UploadImage
												onImageUploaded={(fileUrl) => field.onChange(fileUrl)}
												defaultValue={field.value}
												getFileUrl={({ payload }) =>
													getDepositBannerFileUrl({
														payload: { fileExtension: payload.fileExtension },
													})
												}
												aspectRatio="16/9"
												previewHeight={150}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="shortDescription"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											คำอธิบายสั้น
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="กรอกคำอธิบายสั้น"
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											คำอธิบายเพิ่มเติม
										</FormLabel>
										<FormControl>
											<Editor
												value={field.value || ''}
												onChange={field.onChange}
												placeholder="กรอกคำอธิบาย"
												className="min-h-[200px] [&_.ql-container]:!min-h-[150px]"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				)}

				{/* Action Buttons */}
				<div className="flex flex-col gap-3 pb-4 sm:flex-row">
					<Button
						type="button"
						variant="default"
						size="lg"
						className="w-full"
						onClick={() => {
							form.handleSubmit((values) => {
								setPendingSubmit(() => () => handleSubmit(values))
								setShowRtpConfirm(true)
							}, showFormValidationToast)()
						}}
					>
						{mode === 'create' ? 'สร้างโปรโมชั่น' : 'แก้ไขโปรโมชั่น'}
					</Button>
					<Button
						type="button"
						variant="outline"
						size="lg"
						className="w-full"
						onClick={() => navigate(-1)}
					>
						ยกเลิก
					</Button>
				</div>

				<RtpConfirmDialog
					open={showRtpConfirm}
					onOpenChange={setShowRtpConfirm}
					onConfirm={() => {
						setShowRtpConfirm(false)
						pendingSubmit?.()
						setPendingSubmit(null)
					}}
					confirmLabel={mode === 'create' ? 'สร้างโปรโมชั่น' : 'แก้ไขโปรโมชั่น'}
				/>
			</form>
		</FormProvider>
	)
}
